import { html, fetching, nothing } from 'omni-ui';
import { csvParse } from '../../om2-util.js';
import { om2alert } from '../../om2-alert.js';

/* eslint-disable class-methods-use-this */

/* This let's us build-up class names incrementally when we merge. One special case is
 * for removing classes if the newClass string contains classnames starting with '--'.
 *
 * @param {string} originalClass - The original CSS class string
 * @param {string} [newClass] - The additional CSS class string to add
 * @returns {string} The merged CSS class string
 */
export function mergeClass(originalClass, newClass) {
  if (!newClass) return originalClass;

  let result = `${originalClass} ${newClass}`;
  // If the css class starts with double hyphen (not valid in css spec), remove from results
  const toRemove = newClass.split(/\s+/).filter(x => x.startsWith('--'));
  if (toRemove.length > 0) {
    result = result
      .split(/\s+/)
      .filter(x => !toRemove.includes(x) && !toRemove.includes(`--${x}`))
      .join(' ');
  }
  return result;
}

/* Recursively merge two schema objects (nested dicts etc). The second argument takes
 * precedence and will overwrite the first. We special case when merging class strings.
 *
 * @param {object} schema1 - The first schema object
 * @param {object} schema2 - The second schema object to apply over the first
 * @returns {object} The merged schema object
 */
export function mergeSchema(schema1, schema2) {
  const target = { ...schema1 }; // Shallow copy
  const isObject = o => o && typeof o === 'object' && !Array.isArray(o);
  const merge = (to, from) => {
    if (isObject(to) && isObject(from)) {
      /* eslint-disable no-param-reassign */
      to = { ...to }; // Make a shallow copy of "to" before modifying
      Object.entries(from).forEach(([key, newVal]) => {
        const originalVal = to[key];
        if (
          key === 'class' &&
          typeof originalVal === 'string' &&
          typeof newVal === 'string'
        ) {
          to[key] = mergeClass(originalVal, newVal);
        } else {
          to[key] = merge(originalVal, newVal);
        }
      });
      /* eslint-enable no-param-reassign */
      return to;
    }
    // Overwrite with from, unless both to & from are dict/objects
    return from;
  };
  return merge(target, schema2);
}

export async function fetchEnumOptions(url, self) {
  try {
    // If url is of the format event://eventname or event://eventname/path, fire an
    // event and wait for an event handler to call the resolve() function with
    // results. The results should be an array of objects, where each object has id
    // and name properties, same as the JSON or CSV results.

    // eslint-disable-next-line prefer-regex-literals
    const eventURL = url.match(new RegExp('^event://([^/]+)(?:/?(.*))$'));
    if (eventURL) {
      return await new Promise((resolve, reject) => {
        self.dispatchNewEvent(eventURL[1], {
          detail: { resolve, reject, url, path: eventURL[2] },
        });
      });
    }
    const response = await fetching(url);
    const text = await response.text();
    let options;
    try {
      // Might be json
      options = JSON.parse(text);
    } catch {
      // Not json so try CSV
      options = csvParse(text);
    }
    return options;
  } catch (e) {
    om2alert('Fetching options failed: ', e);
    throw e;
  }
}

export function wrapWithLink(url, innerHTML) {
  try {
    // If we have a valid url of with an allowed protocol
    if (
      url &&
      ['tel:', 'mailto:', 'http:', 'https:'].includes(
        new URL(url, window.location.href).protocol
      )
    ) {
      return html`<a href="${url}" style="display:contents" target="_blank"
        >${innerHTML}</a
      >`;
    }
  } catch {
    // ignore
  }
  return innerHTML;
}

/**
 * Render an error message
 *
 * @param {string} message
 */
export function renderError(message) {
  return html`<p class="has-text-danger">${message}</p>`;
}

/**
 * Render an omni-tooltip
 *
 * @param {object} schema
 * @param {string} [icon] - The tooltip icon
 * @returns {html} The rendered output
 */
export function renderTooltip(schema, icon = 'omni:informative:info') {
  if (!schema.description && !schema.tooltip) return nothing;
  return html`
    <omni-tooltip class="ml-1">
      <omni-icon
        slot="invoker"
        class="is-size-3 is-clickable"
        icon-id=${icon}
      ></omni-icon>
      <div slot="content">${schema.description || schema.tooltip}</div>
    </omni-tooltip>
  `;
}

/**
 * For the most part only a schema's "type" property is used to determine which form
 * field component should render. However for dropdown/radio/twopane we also check
 * whether there is an "enum" field defined as well as whether the "radio" or
 * "twopane" properties are true. Similarly there is some logic for single vs multline
 * string inputs.
 *
 * @param {object} schema - Return a human readable type string for a schema
 * @returns {string} The type of the schema
 */
export const schemaFieldType = (
  {
    type,
    enum: enumField = false,
    radio = false,
    twopane = false,
    multiline = false,
  },
  display = false
) => {
  if (enumField && radio) return 'radio';
  if (enumField && twopane) return 'two-pane';
  if (enumField) return 'dropdown';
  if (type === 'string') {
    if (display) return multiline ? 'textarea' : 'text';
    return multiline ? 'string-multiline' : 'string';
  }
  return type;
};
